import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { Card } from "../../../components/Card";
import { SectionTitle } from "../../../components/SectionTitle";
import { LoadingContainer } from "../../../components/LoadingContainer";
import { spacing } from "@styling/sizing";
import colors from "@styling/colors";
import { fontSizes } from "@styling/fonts";
import { formatMoney } from "../../../utils/formatMoney";
import { toDateString } from "../../../utils/dateUtils";
import { getQuantityOnDate } from "../../../utils/billingItemSegments";
import { BillingItemType } from "@KassaroAB/shared/types/enums/BillingItemType";
import { BillingPeriod } from "@KassaroAB/shared/types/enums/BillingPeriod";
import { Currency } from "@KassaroAB/shared/types/enums/currency";
import { mapBillingPeriodToMonths } from "@KassaroAB/shared/utils/billingPeriod";
import type { RestBillingItem } from "@KassaroAB/shared/types/billingItem";
import type { RestAvailableBillingItem } from "@KassaroAB/shared/types/availableBillingItem";
import type { Money } from "@KassaroAB/shared/types/money";
import type { MultiCurrencyMoney } from "@KassaroAB/shared/types/multiCurrencyMoney";
import type { StoreId } from "@KassaroAB/shared/types/ids";
import { useGetStoreQuery } from "../../../query/storesQuery";

const SectionHeader = styled.div`
  margin-bottom: ${spacing.x2}px;
`;

const LineList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x1}px;
`;

const LineRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: ${spacing.x2}px;
  font-size: ${fontSizes.regular}px;
  color: ${colors.text};
`;

const LineName = styled.span`
  min-width: 0;
`;

const LineMeta = styled.span`
  color: ${colors.textMuted};
  font-size: ${fontSizes.small}px;
`;

const LineAmount = styled.span`
  white-space: nowrap;
  text-align: right;
`;

const LineDiscount = styled.span`
  color: ${colors.success};
  font-size: ${fontSizes.small}px;
  font-weight: 500;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: ${spacing.x2}px;
  margin-top: ${spacing.x2}px;
  padding-top: ${spacing.x2}px;
  border-top: 1px solid ${colors.border};
  font-size: ${fontSizes.large}px;
  font-weight: 600;
  color: ${colors.text};
`;

const EmptyText = styled.p`
  margin: 0;
  color: ${colors.textMuted};
`;

interface CostLine {
  id: string;
  name: string;
  quantity: number;
  amount: number;
  discountAmount: number;
  currency: Currency;
}

interface MonthlyCostSummarySectionProps {
  billingItems: RestBillingItem[] | undefined;
  availableBillingItems: RestAvailableBillingItem[] | undefined;
  isLoading?: boolean;
}

const pickCurrencyAmount = (
  money: MultiCurrencyMoney | undefined,
  preferred: Currency
): Money | null => {
  if (!money?.currencies) return null;
  return (
    money.currencies[preferred] ??
    Object.values(money.currencies).find(
      (entry): entry is Money => entry != null
    ) ??
    null
  );
};

const getMonthlyUnitAmount = (
  availableItem: RestAvailableBillingItem | undefined,
  billingPeriod: BillingPeriod | null | undefined,
  preferredCurrency: Currency
): Money | null => {
  const pricing = availableItem?.pricing;
  if (!pricing) return null;

  const months = mapBillingPeriodToMonths(
    billingPeriod ?? BillingPeriod.MONTHLY
  );
  if (months >= 36) {
    const prepaid = pickCurrencyAmount(
      pricing.prepaid36MonthPrice,
      preferredCurrency
    );
    if (prepaid) return prepaid;
  }
  return pickCurrencyAmount(pricing.monthlyPrice, preferredCurrency);
};

export const MonthlyCostSummarySection = ({
  billingItems,
  availableBillingItems,
  isLoading = false,
}: MonthlyCostSummarySectionProps) => {
  const { t } = useTranslation();
  const { storeId } = useParams<{ storeId: StoreId }>();
  const { data: store } = useGetStoreQuery(storeId);
  const storeCurrency = store?.currency ?? Currency.SEK;
  const today = toDateString(new Date());
  const lookup = new Map(
    (availableBillingItems || []).map((item) => [item.id, item])
  );

  const costLines: CostLine[] = (billingItems ?? [])
    .filter((billingItem) => {
      if (
        billingItem.type !== BillingItemType.PACKAGE &&
        billingItem.type !== BillingItemType.ADD_ON &&
        billingItem.type !== BillingItemType.EXTRA_DEVICE &&
        billingItem.type !== BillingItemType.PAYMENT_METHOD
      ) {
        return false;
      }
      return (
        getQuantityOnDate(
          [billingItem],
          billingItem.availableBillingItemId,
          today
        ) > 0
      );
    })
    .map((billingItem) => {
      const availableItem = lookup.get(billingItem.availableBillingItemId);
      const quantity = getQuantityOnDate(
        [billingItem],
        billingItem.availableBillingItemId,
        today
      );
      const unitPrice = getMonthlyUnitAmount(
        availableItem,
        billingItem.billingPeriod,
        storeCurrency
      );
      const unitAmount = unitPrice?.amount ?? 0;
      const discountAmount = Math.min(
        billingItem.discount?.amount ?? 0,
        unitAmount * quantity
      );
      return {
        id: billingItem.id,
        name:
          availableItem?.name ?? billingItem.availableBillingItemId,
        quantity,
        amount: Math.max(0, unitAmount * quantity - discountAmount),
        discountAmount,
        currency: unitPrice?.currency ?? storeCurrency,
      };
    })
    .filter((line) => line.amount > 0 || line.quantity > 0);

  const totalsByCurrency = costLines.reduce<Partial<Record<Currency, number>>>(
    (totals, line) => {
      totals[line.currency] = (totals[line.currency] ?? 0) + line.amount;
      return totals;
    },
    {}
  );

  return (
    <Card>
      <SectionHeader>
        <SectionTitle>{t("packageSelection.monthlyCostSummary")}</SectionTitle>
      </SectionHeader>

      {isLoading ? (
        <LoadingContainer message={t("packageSelection.loading")} />
      ) : costLines.length === 0 ? (
        <EmptyText>{t("packageSelection.noMonthlyCost")}</EmptyText>
      ) : (
        <>
          <LineList>
            {costLines.map((line) => (
              <LineRow key={line.id}>
                <LineName>
                  {line.name}
                  {line.quantity > 1 && (
                    <LineMeta>
                      {" "}
                      × {line.quantity}
                    </LineMeta>
                  )}
                </LineName>
                <LineAmount>
                  {formatMoney({
                    amount: line.amount,
                    currency: line.currency,
                  })}
                  {t("packageSelection.perMonth")}
                  {line.discountAmount > 0 && (
                    <>
                      {" "}
                      <LineDiscount>
                        {t("packageSelection.discountOff", {
                          amount: formatMoney({
                            amount: line.discountAmount,
                            currency: line.currency,
                          }),
                        })}
                      </LineDiscount>
                    </>
                  )}
                </LineAmount>
              </LineRow>
            ))}
          </LineList>
          {Object.entries(totalsByCurrency).map(([currency, totalAmount]) => (
            <TotalRow key={currency}>
              <span>{t("packageSelection.monthlyCostTotal")}</span>
              <span>
                {formatMoney({
                  amount: totalAmount ?? 0,
                  currency: currency as Currency,
                })}
                {t("packageSelection.perMonth")}
              </span>
            </TotalRow>
          ))}
        </>
      )}
    </Card>
  );
};
